import { CompletionMark } from "../components/ProcessCompletion";
import "../styles/processForm.css";
import IntegratedPersonPhotoInput, { isIntegratedPersonPhoto } from '../components/IntegratedPersonPhotoInput';
import IntegratedAcademicSelect, { isIntegratedAcademicField } from '../components/IntegratedAcademicSelect';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import api from "../api";
import FileAttachment from "../components/FileAttachment";
import DecimalInput from "../components/DecimalInput";
import IntegerInput from "../components/IntegerInput";
import MaskedInput from "../components/MaskedInput";
import { resolveAssetUrl } from "../utils/assets";
import {
  getMaxUploadFileSizeMessage,
  getUploadLimitExceededMessage,
  isFileWithinUploadLimit,
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_MB,
} from "../utils/uploadLimits";
import { applyAttachmentTemplate } from "../utils/attachmentNaming";
import TableField from "../components/TableField";
import usePendingFileUploads from "../hooks/usePendingFileUploads";
import { evaluateVisibilityExpression } from "../utils/safeExpression";
import { generateDocumentPdf } from "../services/documentPdfService";


const getAutoFillValue = (source) => {
  if (!source) return "";
  const today = new Date().toISOString().slice(0, 10);
  if (source === "today_date") return today;
  return "";
};

const applyAutoFillDefaults = (fields = [], values = {}) => {
  const nextValues = { ...values };
  fields.forEach((field) => {
    if (!field?.id || !field.autoFillSource) return;
    const currentValue = nextValues[field.id];
    const isEmpty = currentValue === undefined || currentValue === null || currentValue === "";
    if (!isEmpty) return;
    const prefilled = getAutoFillValue(field.autoFillSource);
    if (prefilled !== "") nextValues[field.id] = prefilled;
  });
  return nextValues;
};

function fieldVisible(field, values) {
  return evaluateVisibilityExpression(field?.visibleWhen, values);
}

function isFieldReadOnly(field) {
  return Boolean(field?.readOnly) ||
    (Boolean(field?.autoFillSource) && field?.autoFillEditable === false);
}

function buildShareMessage(process, form) {
  if (process?.name) return process.name;
  return form?.name || "Formulário";
}

const formatTaxId = (value) => {
  if (!value) return null;
  const digits = `${value}`.replace(/\D/g, "");
  if (digits.length === 14) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  }
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  return value;
};

const normalizeWebsite = (value) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const getWebsiteLabel = (value) => {
  if (!value) return "";
  try {
    const url = new URL(normalizeWebsite(value));
    return url.hostname.replace(/^www\./i, "");
  } catch {
    return value.replace(/^https?:\/\//i, "");
  }
};

const stripDigits = (value) => `${value || ""}`.replace(/\D/g, "");

const isValidCPF = (value) => {
  const digits = stripDigits(value);
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const calcDigit = (base) => {
    let sum = 0;
    for (let i = 0; i < base.length; i += 1) {
      sum += parseInt(base[i], 10) * (base.length + 1 - i);
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const firstDigit = calcDigit(digits.slice(0, 9));
  if (firstDigit !== parseInt(digits[9], 10)) return false;

  const secondDigit = calcDigit(digits.slice(0, 10));
  return secondDigit === parseInt(digits[10], 10);
};

const isValidCNPJ = (value) => {
  const digits = stripDigits(value);
  if (digits.length !== 14) return false;
  if (/^(\\d)\\1{13}$/.test(digits)) return false;

  const calcDigit = (base, weights) => {
    let sum = 0;
    for (let i = 0; i < base.length; i += 1) {
      sum += parseInt(base[i], 10) * weights[i];
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const secondWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const firstDigit = calcDigit(digits.slice(0, 12), firstWeights);
  if (firstDigit !== parseInt(digits[12], 10)) return false;

  const secondDigit = calcDigit(digits.slice(0, 13), secondWeights);
  return secondDigit === parseInt(digits[13], 10);
};

const isValidEmail = (value) => {
  if (!value) return true;
  const email = String(value).trim();
  if (!email) return true;
  const pattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  return pattern.test(email);
};

const fieldSizeClass = {
  1: "md:col-span-1",
  2: "md:col-span-2",
  3: "md:col-span-3",
  4: "md:col-span-4",
  5: "md:col-span-5",
  6: "md:col-span-6",
  7: "md:col-span-7",
  8: "md:col-span-8",
  9: "md:col-span-9",
  10: "md:col-span-10",
  11: "md:col-span-11",
  12: "md:col-span-12",
};

const getFieldLayoutClass = (size) => `col-span-1 ${fieldSizeClass[size] || fieldSizeClass[12]}`;

const clampProgress = (value) => Math.max(0, Math.min(100, Math.round(value)));

const getProgressPercent = (event, fallback = 0) => {
  if (event?.total) return clampProgress((event.loaded / event.total) * 100);
  if (event?.progress) return clampProgress(event.progress * 100);
  return fallback;
};

const LoadingProgressBar = ({ label, progress }) => {
  const safeProgress = clampProgress(progress);

  return (
    <div
      className="w-full"
      role="status"
      aria-live="polite"
      aria-label={`${label}: ${safeProgress}%`}
    >
      <div className="mb-2 flex items-center justify-between text-sm font-medium text-blue-700">
        <span>{label}</span>
        <span className="text-xs font-semibold text-blue-500">{safeProgress}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-blue-100 shadow-inner">
        <div
          className="h-full rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500 transition-all duration-300 ease-out"
          style={{ width: `${safeProgress}%` }}
        />
      </div>
    </div>
  );
};

export default function ExternalForm() {
  const { token } = useParams();
  const location = useLocation();
  const isExternalStepTask = location.pathname.includes("/tarefas-externas/");
  const [form, setForm] = useState(null);
  const [processInfo, setProcessInfo] = useState(null);
  const [company, setCompany] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadError, setLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const {
    pendingUploadsRef,
    pendingUploads,
    pendingUploadsCount,
    hasPendingUploads,
    startUpload,
    updateUpload,
    finishUpload,
    getUpload,
    getTableRowUploadCount,
    getTableUploadCount,
  } = usePendingFileUploads();
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [success, setSuccess] = useState(null);
  const submitLock = useRef(false);
  const [requestInstanceId, setRequestInstanceId] = useState(null);
  const [generatingDocumentFieldId, setGeneratingDocumentFieldId] = useState(null);
  const companyLogoUrl = useMemo(() => resolveAssetUrl(company?.logoUrl), [company?.logoUrl]);

  useEffect(() => {
    let cancelled = false;
    let hideLoadingTimer;

    setLoading(true);
    setLoadProgress(5);
    setLoadError("");
    setSubmitError("");
    setSuccess(null);
    setValues({});
    setCompany(null);
    setRequestInstanceId(null);
    setGeneratingDocumentFieldId(null);
    const endpoint = isExternalStepTask ? `/public/step-tasks/${token}` : `/public/forms/${token}`;
    api
      .get(endpoint, {
        onDownloadProgress: (event) => {
          if (cancelled) return;
          const percent = getProgressPercent(event, 0);
          if (percent > 0) setLoadProgress(Math.min(90, Math.max(10, percent)));
        },
      })
      .then((res) => {
        if (cancelled) return;
        setLoadProgress((current) => Math.max(current, 92));
        setForm(res.data.form);
        setProcessInfo(res.data.process);
        setRequestInstanceId(res.data.instanceId ?? null);
        const initialValues = (res.data.formData && typeof res.data.formData === "object") ? res.data.formData : {};
        const schemaFields = Array.isArray(res.data.form?.schema?.fields) ? res.data.form.schema.fields : [];
        setValues(applyAutoFillDefaults(schemaFields, initialValues));
        const companyData = res.data.company;
        if (companyData) {
          setCompany({
            ...companyData,
            logoUrl: companyData.logoUrl || companyData.logo ? '/company/public/logo' : '',
          });
        } else {
          setCompany(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err.response?.data?.error || (isExternalStepTask ? "Solicitação já respondida" : "Formulário indisponível");
        setLoadError(msg);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadProgress(100);
        hideLoadingTimer = setTimeout(() => {
          if (!cancelled) setLoading(false);
        }, 250);
      });

    return () => {
      cancelled = true;
      clearTimeout(hideLoadingTimer);
    };
  }, [token, isExternalStepTask]);

  useEffect(() => {
    if (!loading) return undefined;
    const timer = setInterval(() => {
      setLoadProgress((current) => Math.min(95, current + (current < 60 ? 8 : current < 85 ? 3 : 1)));
    }, 450);
    return () => clearInterval(timer);
  }, [loading]);

  useEffect(() => {
    if (!submitting) return undefined;
    const timer = setInterval(() => {
      setSubmitProgress((current) => Math.min(95, current + (current < 60 ? 8 : current < 85 ? 3 : 1)));
    }, 450);
    return () => clearInterval(timer);
  }, [submitting]);

  const fillAddressFromCep = async (field, cepValue) => {
    const cep = stripDigits(cepValue);
    if (cep.length !== 8) return;
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (data?.erro) return;
      const addressValues = {
        'address.street': data.logradouro || '',
        'address.neighborhood': data.bairro || '',
        'address.city': data.localidade || '',
        'address.state': data.uf || '',
      };
      const schemaFields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
      setValues((current) => {
        const updated = { ...current };
        schemaFields.forEach((candidate) => {
          if (candidate.groupId !== field.groupId) return;
          const moduleField = candidate.moduleBinding?.moduleField;
          if (moduleField && addressValues[moduleField] !== undefined && !updated[candidate.id]) {
            updated[candidate.id] = addressValues[moduleField];
          }
        });
        return updated;
      });
    } catch (_) {
      // CEP manual continua disponível se a busca externa falhar.
    }
  };

  const handleChange = (event) => {
    const { name, value, options, multiple } = event.target;
    const nextValue = multiple
      ? Array.from(options)
          .filter((option) => option.selected)
          .map((option) => option.value)
      : value;

    const changedField = form?.schema?.fields?.find((field) => field.id === name);
    setValues((prev) => ({ ...prev, [name]: nextValue }));
    if (changedField?.type === 'cep') {
      fillAddressFromCep(changedField, nextValue);
    }
  };

  const handleFileChange = async (name, file) => {
    if (!file) return;
    if (!isFileWithinUploadLimit(file)) {
      alert(getMaxUploadFileSizeMessage());
      return;
    }
    const field = form?.schema?.fields?.find((f) => f.id === name);
    const schemaFields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
    let selectedFile = file;
    let generatedFileName = null;
    if (field?.fileNameTemplate) {
      const { file: renamedFile, generatedName } = applyAttachmentTemplate(file, field.fileNameTemplate, {
        values,
        fields: schemaFields,
        originalFileName: file.name,
      });
      selectedFile = renamedFile;
      generatedFileName = generatedName;
    }
    const data = new FormData();
    data.append("file", selectedFile);
    const category =
      processInfo?.category || processInfo?.name || form?.name || "processo-externo";
    const formName = form?.name || processInfo?.name || "Formulário";
    const fieldTitle = field?.label || name;
    data.append("category", category);
    data.append("formName", formName);
    data.append("fieldTitle", fieldTitle);
    data.append("insertedAt", new Date().toISOString());
    if (generatedFileName) {
      data.append("generatedFileName", generatedFileName);
    }
    const uploadKey = `field:${name}`;
    startUpload(uploadKey, selectedFile.name || file.name);
    try {
      const uploadEndpoint = isExternalStepTask
        ? `/upload/external/step-tasks/${token}`
        : `/upload/external/forms/${token}`;
      const res = await api.post(uploadEndpoint, data, {
        onUploadProgress: (event) => {
          const percent = event.total ? Math.round((event.loaded * 100) / event.total) : 0;
          updateUpload(uploadKey, { percent, status: percent >= 100 ? "processing" : "uploading" });
        },
      });
      setValues((prev) => ({
        ...prev,
        [name]: {
          url: res.data.url,
          downloadUrl: res.data.downloadUrl,
          downloadPath: res.data.downloadPath,
          relativePath: res.data.relativePath,
          name: res.data.name,
          metadata: res.data.metadata,
        },
      }));
    } catch (err) {
      const msg =
        err?.response?.status === 413
          ? getUploadLimitExceededMessage()
          : err.response?.data?.error || "Falha ao enviar arquivo";
      alert(msg);
    } finally {
      finishUpload(uploadKey);
    }
  };

  const fields = useMemo(
    () => (Array.isArray(form?.schema?.fields) ? form.schema.fields.filter((field) => field?.type !== "linkedField") : []),
    [form]
  );
  const groups = useMemo(() => form?.schema?.groups || [], [form]);
  const showCompanyHeader = form?.schema?.settings?.showCompanyHeader !== false;

  const visibleGroupedFields = useMemo(() => {
    const grouped = new Map();
    for (const f of fields) {
      const gid = f.groupId || "__ungrouped__";
      if (!grouped.has(gid)) grouped.set(gid, []);
      if (fieldVisible(f, values)) {
        grouped.get(gid).push(f);
      }
    }
    return grouped;
  }, [fields, values]);

  const handleTableFileChange = async (field, rowIndex, column, file) => {
    if (!file) return;
    if (!isFileWithinUploadLimit(file)) {
      alert(getMaxUploadFileSizeMessage());
      return;
    }

    const data = new FormData();
    data.append("file", file);
    const category = processInfo?.category || processInfo?.name || form?.name || "processo-externo";
    const formName = form?.name || processInfo?.name || "Formulário";
    data.append("category", category);
    data.append("formName", formName);
    data.append("fieldTitle", `${field?.label || field?.id} - ${column?.label || column?.id}`);
    data.append("insertedAt", new Date().toISOString());

    const uploadKey = `table:${field.id}:${rowIndex}:${column.id}`;
    startUpload(uploadKey, file.name);
    try {
      const uploadEndpoint = isExternalStepTask
        ? `/upload/external/step-tasks/${token}`
        : `/upload/external/forms/${token}`;
      const response = await api.post(uploadEndpoint, data, {
        onUploadProgress: (event) => {
          const percent = event.total ? Math.round((event.loaded * 100) / event.total) : 0;
          updateUpload(uploadKey, { percent, status: percent >= 100 ? "processing" : "uploading" });
        },
      });
      const fileInfo = {
        url: response.data.url,
        downloadUrl: response.data.downloadUrl,
        downloadPath: response.data.downloadPath,
        relativePath: response.data.relativePath,
        name: response.data.name,
        metadata: response.data.metadata,
      };
      setValues((prev) => {
        const rows = Array.isArray(prev[field.id]) ? [...prev[field.id]] : [];
        const row = rows[rowIndex] && typeof rows[rowIndex] === "object" ? rows[rowIndex] : {};
        rows[rowIndex] = {
          ...row,
          [column.id]: fileInfo,
        };
        return { ...prev, [field.id]: rows };
      });
    } catch (error) {
      const message =
        error?.response?.status === 413
          ? getUploadLimitExceededMessage()
          : error?.response?.data?.error || error?.message || "Erro ao enviar arquivo";
      alert(message);
    } finally {
      finishUpload(uploadKey);
    }
  };

  const handleGenerateDocumentPdf = async (field) => {
    setGeneratingDocumentFieldId(field.id);
    const openedPdfWindow = typeof window !== "undefined" ? window.open("", "_blank") : null;
    try {
      await generateDocumentPdf({
        field,
        fields: form?.schema?.fields || [],
        values,
        instance: requestInstanceId ? { id: requestInstanceId } : null,
        company,
        openedPdfWindow,
      });
    } catch (error) {
      alert(error?.message || "Não foi possível gerar o documento PDF.");
    } finally {
      setGeneratingDocumentFieldId(null);
    }
  };

  const renderField = (field) => {
    const readOnly = isFieldReadOnly(field);
    const commonProps = {
      id: field.id,
      name: field.id,
      value: values[field.id] ?? "",
      onChange: handleChange,
      required: Boolean(field.required) && !readOnly,
      className:
        `w-full rounded-lg border-2 px-3 py-2 text-sm shadow focus:outline-none focus:ring-2 ${
          readOnly
            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500 focus:border-slate-200 focus:ring-0"
            : "border-blue-200 bg-blue-50/80 text-slate-800 placeholder:text-slate-500 focus:border-blue-500 focus:bg-white focus:ring-blue-200"
        }`,
      readOnly,
    };
    const inputClassName = commonProps.className;

    if (field.type === "document") {
      const isGenerating = generatingDocumentFieldId === field.id;
      return (
        <button
          type="button"
          onClick={() => handleGenerateDocumentPdf(field)}
          disabled={Boolean(generatingDocumentFieldId)}
          className="inline-flex min-w-[240px] items-center justify-center rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100 disabled:cursor-wait disabled:opacity-70"
        >
          {isGenerating
            ? "Gerando PDF..."
            : `Gerar PDF: ${field.documentTemplate?.title || field.label}`}
        </button>
      );
    }

    if (field.type === "table") {
      return (
        <TableField
          field={field}
          value={values[field.id]}
          onChange={(nextRows) => setValues((prev) => ({ ...prev, [field.id]: nextRows }))}
          readOnly={readOnly}
          inputClassName={inputClassName}
          onUploadFile={readOnly ? undefined : ({ rowIndex, column, file }) => handleTableFileChange(field, rowIndex, column, file)}
          getCellUpload={(rowIndex, columnId) => getUpload(`table:${field.id}:${rowIndex}:${columnId}`)}
          getRowPendingUploadCount={(rowIndex) => getTableRowUploadCount(field.id, rowIndex)}
          getTablePendingUploadCount={() => getTableUploadCount(field.id)}
        />
      );
    }

    if (isIntegratedAcademicField(field)) {
      return <IntegratedAcademicSelect field={field} fields={(form?.schema?.fields || [])} values={values} disabled={readOnly} className={inputClassName} onChange={patch => setValues(prev => ({ ...prev, ...patch }))} />;
    }
    if (field.type === "list") {
      const selectValue = field.allowMultiple
        ? Array.isArray(values[field.id])
          ? values[field.id]
          : values[field.id]
            ? [values[field.id]]
            : []
        : values[field.id] || "";

      return (
        <select {...commonProps} disabled={commonProps.readOnly} multiple={field.allowMultiple} value={selectValue}>
          {!field.allowMultiple && (
            <option value="" disabled>
              Selecione...
            </option>
          )}
          {(field.options || []).map((option, idx) => (
            <option key={idx} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }
    if (isIntegratedPersonPhoto(field)) {
      return <IntegratedPersonPhotoInput field={field} value={values[field.id]} readOnly={readOnly} onChange={photo => setValues(previous => ({ ...previous, [field.id]: photo }))} />;
    }
    if (field.type === "file") {
      const current = values[field.id];
      if (readOnly) {
        return current
          ? <FileAttachment file={current} />
          : <p className="text-sm text-slate-500">Nenhum arquivo anexado.</p>;
      }
      const hint = `Tamanho máximo do arquivo: ${MAX_UPLOAD_FILE_SIZE_MB} MB.`;
      const fieldUpload = getUpload(`field:${field.id}`);
      return current && !fieldUpload ? (
        <div className="space-y-2">
          <FileAttachment file={current} />
          <p className="text-xs text-slate-500">{hint}</p>
        </div>
      ) : (
        <div className="space-y-2">
          <input
            type="file"
            onChange={(event) => {
              const selectedFile = event.target.files?.[0];
              if (!selectedFile) return;
              if (selectedFile.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
                alert(getMaxUploadFileSizeMessage());
                event.target.value = "";
                return;
              }
              handleFileChange(field.id, selectedFile);
            }}
            required={field.required}
            disabled={Boolean(fieldUpload)}
            className="w-full rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/80 px-3 py-2 text-sm text-slate-800 shadow focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          {current && <FileAttachment file={current} />}
          {fieldUpload && (
            <p className="text-xs text-blue-600">
              {fieldUpload.fileName} — {fieldUpload.status === "processing" ? "Processando arquivo..." : `${fieldUpload.percent || 0}%`}
            </p>
          )}
          <p className="text-xs text-slate-500">{hint}</p>
        </div>
      );
    }
    if (field.type === "decimal") {
      return (
        <DecimalInput
          {...commonProps}
          decimalPlaces={field.decimalPlaces || 2}
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "number") {
      return (
        <IntegerInput
          {...commonProps}
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "phone") {
      return (
        <MaskedInput
          {...commonProps}
          mask="phone"
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "currency") {
      return (
        <MaskedInput
          {...commonProps}
          mask="currency"
          placeholder={field.placeholder || "R$ 0,00"}
        />
      );
    }

    if (field.type === "cpf" || field.mask === "cpf") {
      return (
        <MaskedInput
          {...commonProps}
          mask="cpf"
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "cnpj") {
      return (
        <MaskedInput
          {...commonProps}
          mask="cnpj"
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "cep") {
      return (
        <MaskedInput
          {...commonProps}
          mask="cep"
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "email") {
      return (
        <input
          {...commonProps}
          type="email"
          inputMode="email"
          autoComplete="email"
          pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "date") {
      return (
        <input
          {...commonProps}
          type="date"
          onFocus={(event) => event.target.showPicker?.()}
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "time") {
      return (
        <input
          {...commonProps}
          type="time"
          step={field.step || 60}
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "textarea") {
      const textareaRows = Math.max(3, Number(field.rows) || 0);
      return (
        <textarea
          {...commonProps}
          rows={textareaRows}
          placeholder={field.placeholder || ""}
          className={`${inputClassName} resize-y`}
        />
      );
    }
    return (
      <input
        {...commonProps}
        type="text"
        placeholder={field.placeholder || ""}
      />
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form || submitLock.current) return;
    const pendingCount = pendingUploadsRef.current.size;
    if (pendingCount > 0) {
      alert(`Aguarde o término do envio de ${pendingCount} arquivo(s).`);
      return;
    }

    const requiredFields = fields.filter(
      (f) => f.required && f.type !== "document" && !isFieldReadOnly(f) && fieldVisible(f, values)
    );
    for (const field of requiredFields) {
      const value = values[field.id];
      if (field.type === "table") {
        const rows = Array.isArray(value) ? value : [];
        if (rows.length === 0) {
          alert(`Adicione ao menos uma linha no campo "${field.label}".`);
          return;
        }
        const requiredColumns = Array.isArray(field.tableColumns)
          ? field.tableColumns.filter((column) => column.required)
          : [];
        for (const column of requiredColumns) {
          const hasMissing = rows.some((row) => {
            const cell = row?.[column.id];
            return cell === undefined || cell === null || cell === "";
          });
          if (hasMissing) {
            alert(`Preencha a coluna obrigatória "${column.label}" em "${field.label}".`);
            return;
          }
        }
        continue;
      }
      const isEmptyList = Array.isArray(value) && value.length === 0;
      if (value === undefined || value === "" || isEmptyList) {
        alert(`O campo "${field.label}" é obrigatório.`);
        return;
      }
    }

    for (const field of fields) {
      if (field.type === "document" || isFieldReadOnly(field) || !fieldVisible(field, values)) continue;
      const value = values[field.id];
      if (!value) continue;
      if (field.type === "cpf" && !isValidCPF(value)) {
        alert(`Informe um CPF válido no campo "${field.label}".`);
        return;
      }
      if (field.type === "cnpj" && !isValidCNPJ(value)) {
        alert(`Informe um CNPJ válido no campo "${field.label}".`);
        return;
      }
      if (field.type === "email" && !isValidEmail(value)) {
        alert(`Informe um e-mail válido no campo "${field.label}".`);
        return;
      }
    }

    submitLock.current = true;
    setSubmitting(true);
    setSubmitProgress(5);
    setSubmitError("");
    try {
      const submitEndpoint = isExternalStepTask ? `/public/step-tasks/${token}/submit` : `/public/forms/${token}/submit`;
      const res = await api.post(submitEndpoint, values, {
        onUploadProgress: (event) => {
          const percent = getProgressPercent(event, 0);
          if (percent > 0) setSubmitProgress(Math.min(70, Math.max(10, percent * 0.7)));
        },
        onDownloadProgress: (event) => {
          const percent = getProgressPercent(event, 0);
          if (percent > 0) setSubmitProgress(Math.min(95, Math.max(70, 70 + percent * 0.25)));
        },
      });
      setSubmitProgress(100);
      const submittedInstanceId =
        res.data?.instanceId ?? res.data?.instance?.id ?? res.data?.id ?? requestInstanceId;
      setRequestInstanceId(submittedInstanceId ?? null);
      setSuccess({
        instanceId: submittedInstanceId,
        done: res.data?.done,
      });
      setValues({});
    } catch (err) {
      const msg = err.response?.data?.error || "Não foi possível enviar sua solicitação.";
      setSubmitError(msg);
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  const title = buildShareMessage(processInfo, form);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:py-12">
      <div className="mx-auto w-full max-w-4xl rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-8">
        {loading ? (
          <div className="py-20">
            <LoadingProgressBar label="Carregando formulário" progress={loadProgress} />
            <p className="mt-4 text-center text-sm text-slate-500">Preparando os campos para preenchimento...</p>
          </div>
        ) : loadError ? (
          <div className="py-20 text-center">
            <h1 className="text-2xl font-semibold text-slate-800">Não foi possível exibir o formulário</h1>
            <p className="mt-3 text-slate-600">{loadError}</p>
          </div>
        ) : success ? (
          <div className="process-completion" role="status">
            <CompletionMark />
            <h1 className="text-3xl font-semibold text-slate-900">Solicitação enviada!</h1>
            <p className="mt-4 text-slate-600">
              Anote o número da sua solicitação para acompanhar com a equipe:
            </p>
            <p className="mx-auto mt-6 w-fit rounded-2xl border border-slate-200 bg-white px-8 py-4 text-4xl font-semibold tracking-tight text-slate-900">#{success.instanceId}</p>
            <button
              onClick={() => {
                setSuccess(null);
                setValues({});
              }}
              className="mt-10 rounded-full bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
            >
              Enviar nova solicitação
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="process-form process-form-flat space-y-8">
            {company && showCompanyHeader && (
              <section className="flex flex-col gap-4 rounded-2xl border border-blue-100 bg-blue-50/70 p-4 shadow-sm sm:flex-row sm:items-center">
                {companyLogoUrl && (
                  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl bg-white shadow-inner">
                    <img
                      src={companyLogoUrl}
                      alt={company.tradeName || company.name}
                      className="max-h-14 w-full object-contain"
                    />
                  </div>
                )}
                <div className="flex-1 space-y-2 text-sm">
                  <div>
                    <p className="text-base font-semibold text-blue-900">
                      {company.tradeName || company.name}
                    </p>
                    {company.tradeName && company.name && company.tradeName.trim() !== company.name.trim() && (
                      <p className="text-xs text-blue-700/80">{company.name}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-blue-700">
                    {company.taxId && (
                      <span className="font-medium">
                        CNPJ: {formatTaxId(company.taxId)}
                      </span>
                    )}
                    {company.website && (
                      <a
                        href={normalizeWebsite(company.website)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-medium text-blue-700 hover:underline"
                      >
                        {getWebsiteLabel(company.website)}
                      </a>
                    )}
                  </div>
                </div>
              </section>
            )}
            <header className="rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white shadow-lg">
              <p className="text-sm uppercase tracking-[0.3em] text-blue-100">
                Formulário externo
              </p>
              <h1 className="mt-2 text-3xl font-semibold">{title}</h1>
              {processInfo?.description && (
                <p className="mt-3 max-w-3xl text-blue-50">
                  {processInfo.description}
                </p>
              )}
            </header>

            {submitError && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
                {submitError}
              </div>
            )}

            {groups.map((group) => {
              const list = visibleGroupedFields.get(group.id) || [];
              if (list.length === 0) return null;
              return (
                <section
                  key={group.id}
                  className="process-form-group rounded-2xl border border-slate-100 bg-slate-50/70 p-6 shadow-sm"
                >
                  <h2 className="text-lg font-semibold text-slate-800">{group.label}</h2>
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-12">
                    {list.map((field) => (
                      <div
                        key={field.id}
                        className={`flex h-full flex-col gap-3 ${getFieldLayoutClass(field.size)}`}
                      >
                        <div className="space-y-1">
                          <label className="text-sm font-medium text-slate-700">
                            {field.label}
                            {field.required && <span className="text-red-600"> *</span>}
                          </label>
                          {field.description && (
                            <p className="text-xs leading-relaxed text-slate-500">
                              {field.description}
                            </p>
                          )}
                        </div>
                        {renderField(field)}
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}

            {visibleGroupedFields.get("__ungrouped__")?.length ? (
              <section className="process-form-group rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                {groups.length > 0 && (
                  <h2 className="text-lg font-semibold text-slate-800">Campos adicionais</h2>
                )}
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-12">
                  {visibleGroupedFields.get("__ungrouped__").map((field) => (
                    <div
                      key={field.id}
                      className={`flex h-full flex-col gap-3 ${getFieldLayoutClass(field.size)}`}
                    >
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-slate-700">
                          {field.label}
                          {field.required && <span className="text-red-600"> *</span>}
                        </label>
                        {field.description && (
                          <p className="text-xs leading-relaxed text-slate-500">
                            {field.description}
                          </p>
                        )}
                      </div>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {submitting && (
              <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4 shadow-sm">
                <LoadingProgressBar label="Enviando formulário" progress={submitProgress} />
                <p className="mt-3 text-sm text-blue-700">Não feche esta página enquanto sua solicitação é enviada.</p>
              </div>
            )}
            {hasPendingUploads && (
              <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4 text-sm text-blue-700 shadow-sm">
                <p className="font-semibold">Aguardando envio de arquivos:</p>
                <ul className="mt-2 space-y-1">
                  {pendingUploads.map((upload) => (
                    <li key={upload.key}>{upload.fileName} — {upload.status === "processing" ? "Processando arquivo..." : `${upload.percent || 0}%`}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-end">
              <button
                type="submit"
                disabled={submitting || hasPendingUploads}
                className="inline-flex items-center justify-center rounded-full bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {hasPendingUploads ? `Aguardando ${pendingUploadsCount} arquivo(s)...` : submitting ? "Enviando..." : "Enviar solicitação"}
              </button>
            </div>
          </form>
        )}
        {!loading && (
          <footer className="mt-10 flex flex-col items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-4 text-center text-xs text-slate-500 sm:flex-row sm:justify-center sm:text-left sm:text-sm">
            <img
              src="/nashlogoazul.png"
              alt="Nash Tecnologia"
              className="h-12 w-auto"
            />
            <p className="max-w-xl">
              Formulário gerado pelo sistema Intelligent BPM Nash Tecnologia. Se quiser saber mais{' '}
              <a
                href="https://www.nashtec.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-blue-600 hover:underline"
              >
                clique aqui
              </a>{' '}
              ou acesse{' '}
              <a
                href="https://www.nashtec.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-blue-600 hover:underline"
              >
                www.nashtec.com.br
              </a>
              .
            </p>
          </footer>
        )}
      </div>
    </div>
  );
}